import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClienteCompromisos, listTags } from '@/api/clientes';
import { Header } from '@/components/Header';
import { MultiSelectChip } from '@/components/MultiSelectChip';
import { EstadoFiltroChip } from '@/components/EstadoFiltroChip';
import { FiltroAvanzadoDrawer } from '@/components/FiltroAvanzadoDrawer';
import { CompromisoQuickViewDrawer } from '@/components/CompromisoQuickViewDrawer';
import { useFiltrosCompromisos } from '@/hooks/useFiltrosCompromisos';
import type { CompromisoConContexto } from '@/types';
import styles from './pages.module.css';

export function ClienteCompromisosPage() {
  const { clienteSlug } = useParams<{ clienteSlug: string }>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickView, setQuickView] = useState<{
    open: boolean;
    compromiso: CompromisoConContexto | null;
  }>({ open: false, compromiso: null });

  const { data, isLoading, error } = useQuery({
    queryKey: ['cliente-compromisos', clienteSlug],
    queryFn: () => getClienteCompromisos(clienteSlug!),
    enabled: !!clienteSlug,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: listTags,
    staleTime: 5 * 60 * 1000,
  });

  const tagsById = useMemo(() => {
    const m = new Map<string, { nombre: string; color: string }>();
    for (const t of allTags) m.set(t.id, { nombre: t.nombre, color: t.color });
    return m;
  }, [allTags]);

  const compromisos = data?.compromisos ?? [];

  const {
    filtros,
    setEstado,
    setPlantas,
    setPropuestas,
    setIniciativas,
    setEvs,
    setTags,
    setSearch,
    limpiarTodo,
    cantidadFiltrosActivos,
    opcionesPlantas,
    opcionesPropuestas,
    opcionesIniciativas,
    opcionesEvs,
    compromisosFiltrados,
  } = useFiltrosCompromisos(compromisos);

  // Conteos por estado (para el chip de estado)
  const estadoCounts = useMemo(
    () => ({
      pendientes: compromisos.filter((c) => c.flags.isPendiente).length,
      bloqueados: compromisos.filter((c) => c.flags.hasBloqueado).length,
      completados: compromisos.filter((c) => c.flags.isCompletado).length,
      todos: compromisos.length,
    }),
    [compromisos],
  );

  if (isLoading) {
    return (
      <>
        <Header breadcrumbs={[{ label: 'Inicio', to: '/' }]} title="Cargando…" />
        <div className={`container ${styles.body}`}>
          <div className="empty">Cargando…</div>
        </div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <Header breadcrumbs={[{ label: 'Inicio', to: '/' }]} title="No encontrado" />
        <div className={`container ${styles.body}`}>
          <div className="empty">No se pudo cargar.</div>
        </div>
      </>
    );
  }

  const { cliente } = data;

  const tituloPagina =
    filtros.estado === 'bloqueados'
      ? 'Compromisos bloqueados'
      : filtros.estado === 'completados'
        ? 'Compromisos completados'
        : filtros.estado === 'todos'
          ? 'Todos los compromisos'
          : 'Compromisos pendientes';

  const subtituloPagina =
    filtros.estado === 'bloqueados'
      ? 'Compromisos con al menos un hito en estado bloqueado, transversales a todas las instalaciones y propuestas del cliente.'
      : filtros.estado === 'completados'
        ? 'Compromisos con todos sus ítems entregados.'
        : filtros.estado === 'todos'
          ? 'Vista completa de todos los compromisos del cliente, sin filtrar por estado.'
          : 'Compromisos no completados (pendientes, on track y bloqueados) en toda la jerarquía del cliente.';

  return (
    <>
      <Header
        breadcrumbs={[
          { label: 'Inicio', to: '/' },
          { label: cliente.nombre, to: `/clientes/${cliente.slug}` },
          { label: tituloPagina },
        ]}
        title={
          <>
            {tituloPagina}
            <span
              style={{
                background: cliente.color,
                color: 'white',
                fontSize: 12,
                padding: '3px 10px',
                borderRadius: 999,
                marginLeft: 12,
                fontWeight: 600,
              }}
            >
              {cliente.nombre}
            </span>
          </>
        }
        subtitle={subtituloPagina}
        meta={
          <>
            <span>{compromisosFiltrados.length} compromisos mostrados</span>
            {cantidadFiltrosActivos > 0 && (
              <span>· {cantidadFiltrosActivos} filtro{cantidadFiltrosActivos === 1 ? '' : 's'} activo{cantidadFiltrosActivos === 1 ? '' : 's'}</span>
            )}
          </>
        }
      />

      <div className={`container ${styles.body}`}>
        {/* Barra de filtros */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            padding: 14,
            marginBottom: 16,
          }}
        >
          {/* Línea 1: chips */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <EstadoFiltroChip
              value={filtros.estado}
              onChange={setEstado}
              counts={estadoCounts}
            />

            <MultiSelectChip
              icon="🏭"
              singular="planta"
              plural="plantas"
              options={opcionesPlantas.map((p) => ({ id: p.slug, label: p.nombre }))}
              selected={filtros.plantas}
              onChange={setPlantas}
            />

            <MultiSelectChip
              icon="📄"
              singular="propuesta"
              plural="propuestas"
              options={opcionesPropuestas.map((p) => ({ id: p.id, label: p.nombre }))}
              selected={filtros.propuestas}
              onChange={setPropuestas}
              hideIfEmpty
            />

            <MultiSelectChip
              icon="🏷"
              singular="tag"
              plural="tags"
              options={allTags.map((t) => ({ id: t.id, label: t.nombre, color: t.color }))}
              selected={filtros.tags}
              onChange={setTags}
            />

            <span style={{ color: 'var(--border)', fontSize: 14 }}>·</span>

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              style={{
                background: cantidadFiltrosActivos > 0 ? 'rgba(20,184,166,0.12)' : 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 999,
                width: 34,
                height: 34,
                padding: 0,
                fontSize: 14,
                cursor: 'pointer',
                color: cantidadFiltrosActivos > 0 ? 'var(--brand)' : 'var(--text-muted)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
              aria-label="Filtro avanzado"
              title="Filtro avanzado"
            >
              ⚙
              {cantidadFiltrosActivos > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    background: 'var(--brand)',
                    color: 'white',
                    fontSize: 9,
                    fontWeight: 700,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 999,
                    padding: '0 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {cantidadFiltrosActivos}
                </span>
              )}
            </button>

            <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: 13,
                  color: 'var(--text-soft)',
                  pointerEvents: 'none',
                }}
              >
                🔍
              </span>
              <input
                type="search"
                placeholder="Buscar compromiso, propuesta o planta…"
                value={filtros.search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '7px 12px 7px 34px',
                  fontSize: 12,
                  border: '1px solid var(--border)',
                  borderRadius: 999,
                  background: 'transparent',
                  color: 'var(--text)',
                }}
              />
            </div>
          </div>

          {/* Línea 2: contador + limpiar */}
          {cantidadFiltrosActivos > 0 && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px dashed var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 11,
                color: 'var(--text-soft)',
              }}
            >
              <span>
                {cantidadFiltrosActivos} filtro{cantidadFiltrosActivos === 1 ? '' : 's'} aplicado
                {cantidadFiltrosActivos === 1 ? '' : 's'} ·{' '}
                <button
                  type="button"
                  onClick={limpiarTodo}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--brand)',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Limpiar todo
                </button>
              </span>
              <span>
                Mostrando {compromisosFiltrados.length} de {compromisos.length} compromisos
              </span>
            </div>
          )}
        </div>

        {/* Cards */}
        {compromisosFiltrados.length === 0 ? (
          <div className="empty">
            {cantidadFiltrosActivos > 0
              ? 'Ningún compromiso coincide con los filtros actuales.'
              : filtros.estado === 'bloqueados'
                ? 'No hay compromisos con bloqueos activos.'
                : 'No hay compromisos para este cliente.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {compromisosFiltrados.map((c) => (
              <CompromisoRow
                key={c.id}
                c={c}
                clienteColor={cliente.color}
                tagsById={tagsById}
                onClick={() => setQuickView({ open: true, compromiso: c })}
              />
            ))}
          </div>
        )}
      </div>

      <FiltroAvanzadoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filtros={filtros}
        cantidadFiltrosActivos={cantidadFiltrosActivos}
        setEstado={setEstado}
        setPlantas={setPlantas}
        setPropuestas={setPropuestas}
        setIniciativas={setIniciativas}
        setEvs={setEvs}
        setTags={setTags}
        limpiarTodo={limpiarTodo}
        opcionesPlantas={opcionesPlantas.map((p) => ({ id: p.slug, label: p.nombre }))}
        opcionesPropuestas={opcionesPropuestas.map((p) => ({ id: p.id, label: p.nombre }))}
        opcionesIniciativas={opcionesIniciativas.map((i) => ({ id: i.id, label: i.nombre }))}
        opcionesEvs={opcionesEvs.map((e) => ({ id: e.id, label: e.nombre, color: e.color }))}
        tags={allTags}
        totalMostrados={compromisosFiltrados.length}
      />

      <CompromisoQuickViewDrawer
        open={quickView.open}
        compromiso={quickView.compromiso}
        clienteSlug={cliente.slug}
        clienteColor={cliente.color}
        tags={allTags}
        onClose={() => setQuickView({ open: false, compromiso: null })}
      />
    </>
  );
}

function CompromisoRow({
  c,
  clienteColor,
  tagsById,
  onClick,
}: {
  c: CompromisoConContexto;
  clienteColor: string;
  tagsById: Map<string, { nombre: string; color: string }>;
  onClick: () => void;
}) {
  const accent = c.flags.hasBloqueado ? '#dc2626' : clienteColor;
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 12,
        padding: '14px 16px',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {c.facility && (
            <span
              style={{
                background: clienteColor + '22',
                color: clienteColor,
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              🏭 {c.facility.nombre}
            </span>
          )}
          {c.propuesta && (
            <span
              style={{
                background: 'var(--surface-2)',
                color: 'var(--text)',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                border: '1px solid var(--border)',
              }}
            >
              📄 {c.propuesta.codigo} · {c.propuesta.nombre}
            </span>
          )}
          {c.flags.hasBloqueado && (
            <span
              style={{
                background: '#fee2e2',
                color: '#b91c1c',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              🚫 {c.hitos.bloqueados} bloqueado{c.hitos.bloqueados > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              background: clienteColor,
              color: 'white',
              padding: '2px 8px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {c.codigo}
          </span>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{c.nombre}</span>
        </div>

        {c.descripcion && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginTop: 4,
              lineHeight: 1.45,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {c.descripcion}
          </div>
        )}

        {c.tagIds.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {c.tagIds.map((tid) => {
              const t = tagsById.get(tid);
              if (!t) return null;
              return (
                <span
                  key={tid}
                  style={{
                    background: t.color + '22',
                    color: t.color,
                    padding: '1px 6px',
                    borderRadius: 999,
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  {t.nombre}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div
        style={{
          flexShrink: 0,
          textAlign: 'right',
          minWidth: 120,
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>{c.hitos.pct}%</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {c.hitos.entregados} / {c.hitos.activos} ítems
        </div>
        {c.hitos.suspendidos > 0 && (
          <div style={{ fontSize: 10, color: 'var(--text-soft)', marginTop: 2 }}>
            ({c.hitos.suspendidos} suspendido{c.hitos.suspendidos > 1 ? 's' : ''})
          </div>
        )}
      </div>
    </div>
  );
}
